import { acceptWin, markWinAsViewed } from "@shared/actions/jackpots";
import { Button } from "@shared/components";
import { useCurrentWin } from "@shared/hooks/jackpots/jackpots";
import { useLobbyParams } from "@shared/hooks/lobby/lobby";
import { $f } from "@shared/hooks/shared";
import { useUserBankGroup } from "@shared/hooks/user";
import { preloadImages, root } from "@shared/utils";
import anime from "animejs";
import cx from "classnames";
import { Howl } from "howler";
import { isNull, random, range } from "lodash-es";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";
import Modal from "react-modal";
import { useDispatch } from "react-redux";

const emptyFunc = () => false;

const getVariantNumber = (slot) => 1 + slot;

const getVariants = (firstVariant, winVariant) => {
  return [
    { variant: firstVariant },
    ...range(0, random(50, 75)).map(() => ({ variant: random(1, 4) })),
    { variant: winVariant },
  ];
};

const getSound = (fileName, preload = false) => {
  return new Howl({
    src: [require(`@shared/assets/sounds/${fileName}`).default],
    preload,
  });
};

export const JackpotModal = memo(() => {
  const [isImagesLoaded, setImagesLoaded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isWinTaking, setIsWinTaking] = useState(false);
  const [isSpinned, setIsSpinned] = useState(false);
  const [isSoundsLoaded, setSoundsLoaded] = useState(false);
  const oldWinId = useRef("");
  const win = useCurrentWin();
  const { currency } = useUserBankGroup() ?? "";
  const { sweepstakes: isSweepstakesEnabled } = useLobbyParams() ?? {};

  const computedCurrency = isSweepstakesEnabled ? "" : currency;

  const dispatch = useDispatch();

  const sounds = useMemo(() => {
    return {
      alert: getSound("alert.mp3"),
      coin: getSound("coin.mp3"),
      wheel: getSound("wheel.mp3"),
      wheelStopped: getSound("wheel-stopped.mp3"),
      congratulations: getSound("congratulations.mp3"),
    };
  }, []);

  const slot = win ? win.slot : null;
  const winVariant = getVariantNumber(slot);

  const [lines] = useState([
    getVariants(1, winVariant),
    getVariants(2, winVariant),
    getVariants(3, winVariant),
    getVariants(4, winVariant),
  ]);

  const loadImages = async () => {
    const getImage = (fileName) => require(`@theme/assets/${fileName}`).default;

    await preloadImages([
      getImage("jackpot-modal__background.png"),
      getImage("jackpot-modal__substrate.png"),
      getImage("jackpot-modal__symbol.png"),
      getImage("jackpot-modal__image.png"),
      getImage("jackpot-modal__button.png"),
    ]);
    setImagesLoaded(true);
  };

  const preloadSounds = useCallback(async () => {
    const soundsMethods = Object.values(sounds);
    const promises = soundsMethods.map(
      (sound) =>
        new Promise((resolve) => {
          sound.once("load", () => resolve());
          sound.load();
        })
    );
    await Promise.all(promises);
    setSoundsLoaded(true);
  }, [sounds]);

  const preparedToCloseModal = (id) => {
    oldWinId.current = id;
    setIsSpinned(false);
    setIsWinTaking(false);
  };

  const takeWin = (id) => {
    dispatch(acceptWin(id));
    Object.values(sounds).forEach((sound) => sound.stop());
    preparedToCloseModal(id);
  };

  const closeModal = () => {
    if (win.state === "canceled") {
      dispatch(markWinAsViewed(win.id));
    }
    preparedToCloseModal(win.id);
  };

  const playAnimation = () => {
    if (isAnimating) {
      return;
    }

    setIsAnimating(true);
    setIsSpinned(false);
    setIsWinTaking(false);

    const $lines = Array.from(
      document.querySelectorAll(".jackpot-modal__line")
    );
    const $symbol = document.querySelector(".jackpot-modal__symbol");

    const getParams = (number) => ({
      targets: $lines[number],
      translateY: [
        0,
        `-${$lines[number].scrollHeight - $symbol.clientHeight}px`,
      ],
    });

    const animation = anime
      .timeline({
        easing: "easeOutExpo",
        duration: 5000,
        autoplay: false,
      })
      .add(getParams(0), 0)
      .add(getParams(1), 0)
      .add(getParams(2), 0)
      .add(getParams(3), 0);

    animation.finished.then(() => {
      sounds.wheel.stop();
      sounds.wheelStopped.play();
      sounds.congratulations.play();
      setIsAnimating(false);
      setIsSpinned(true);
    });

    sounds.coin.play();
    animation.play();
    sounds.wheel.play();
  };

  useEffect(() => {
    preloadSounds();
    loadImages();
  }, []);

  useEffect(() => {
    if (
      win &&
      win.state === "pending" &&
      win.id !== oldWinId.current &&
      !isSpinned &&
      isImagesLoaded &&
      isSoundsLoaded &&
      !isAnimating &&
      !isWinTaking
    ) {
      sounds.alert.play();
    }
  }, [
    isAnimating,
    isImagesLoaded,
    isSoundsLoaded,
    isSpinned,
    isWinTaking,
    sounds.alert,
    win,
    win.state,
  ]);

  const renderGame = () => {
    return [
      <div className="jackpot-modal__frame" key="frame">
        <FormattedMessage id="boilerplate.components.JackpotModal.Game.Title">
          {(message) => <div className="jackpot-modal__title">{message}</div>}
        </FormattedMessage>

        <div className="jackpot-modal__lines">{lines.map(renderLine)}</div>
      </div>,
      <div
        key="button"
        className={cx("jackpot-modal__button", {
          "jackpot-modal__button--disabled": isAnimating,
        })}
        onClick={() => (isAnimating ? emptyFunc : playAnimation())}
      >
        <div className="jackpot-modal__button-background" />
        <FormattedMessage id="boilerplate.components.JackpotModal.Game.Spin">
          {(message) => (
            <div className="jackpot-modal__button-text">{message}</div>
          )}
        </FormattedMessage>
      </div>,
    ];
  };

  const renderCongratulations = () => {
    const winVariant = getVariantNumber(win.slot);
    const isPending = win.state === "pending";

    return [
      <div className="jackpot-modal__frame" key="frame">
        <div
          className={cx(
            "jackpot-modal__image",
            `jackpot-modal__image--variant-${winVariant}`
          )}
        />
        <FormattedMessage id="boilerplate.components.JackpotModal.Congratulations.Title">
          {(message) => <div className="jackpot-modal__title">{message}</div>}
        </FormattedMessage>
        <div className="jackpot-modal__lines">
          <div className="jackpot-modal__prize">
            {computedCurrency} {$f.formatMoney($f.toMoney(win.value))}
          </div>
        </div>
      </div>,

      <div
        key="button"
        className={cx("jackpot-modal__button", {
          "jackpot-modal__button--disabled": isWinTaking,
        })}
        onClick={() =>
          isWinTaking
            ? emptyFunc
            : isPending
            ? takeWin(win.id)
            : dispatch(markWinAsViewed(win.id))
        }
      >
        <div className="jackpot-modal__button-background" />
        {isPending ? (
          <FormattedMessage id="boilerplate.components.JackpotModal.Congratulations.TakeButton">
            {(message) => (
              <div className="jackpot-modal__button-text">{message}</div>
            )}
          </FormattedMessage>
        ) : (
          <FormattedMessage id="boilerplate.components.JackpotModal.Congratulations.OkButton">
            {(message) => (
              <div className="jackpot-modal__button-text">{message}</div>
            )}
          </FormattedMessage>
        )}
      </div>,
    ];
  };

  const renderTimePassed = () => {
    return (
      <div className="modal modal_slide_down modal_login">
        <div
          className="modal__close ico ico-close"
          onClick={() => dispatch(markWinAsViewed(win.id))}
        />
        <FormattedMessage id="boilerplate.components.JackpotModal.TimePassed.Title">
          {(message) => <b className="modal__title">{message}</b>}
        </FormattedMessage>
        <div style={{ flex: 1 }}>
          <FormattedMessage id="boilerplate.components.JackpotModal.TimePassed.Message">
            {(message) => <p style={{ margin: 15 }}>{message}</p>}
          </FormattedMessage>
        </div>

        <div style={{ margin: 15 }}>
          <Button
            variant="primary"
            className="grid__item_3"
            onClick={() => dispatch(markWinAsViewed(win.id))}
          >
            <FormattedMessage id="boilerplate.components.JackpotModal.TimePassed.OkButton" />
          </Button>
        </div>
      </div>
    );
  };

  const renderSymbol = (symbol, index) => (
    <div
      className={`jackpot-modal__symbol jackpot-modal__symbol--variant-${symbol.variant}`}
      key={index}
    />
  );

  const renderLine = (line, index) => {
    return (
      <div className="jackpot-modal__line" key={index}>
        {line.map(renderSymbol)}
      </div>
    );
  };

  const hasWin = !isNull(win);
  let content = null;

  if (win.state === "pending" && !isSpinned) {
    content = renderGame();
  } else if (win.state === "pending" || win.state === "paid") {
    content = renderCongratulations();
  } else if (win.state === "canceled") {
    content = renderTimePassed();
  }

  return (
    <Modal
      appElement={root}
      isOpen={hasWin && isImagesLoaded && isSoundsLoaded}
      onRequestClose={closeModal}
      contentLabel="Jackpot Modal"
      overlayClassName="jackpot-modal"
      className="jackpot-modal__content"
    >
      {content}
    </Modal>
  );
});

JackpotModal.displayName = "JackpotModal";
